import { PrimaryTaxonomy } from '../apis/rec-gateway/rec-gateway.models';
import { EvolveProductTypeKey } from '../apis/sherpath-app-facade-service/sherpath-app-facade-service.dtos';

// ALPHA-ONLY MOCK: the MedEd question banks have no content under the ISBNs Evolve returns yet, so two mock question-bank
// ISBNs are hardcoded and a MedEd course is detected by its parent ISBN (SHER-10999).
//
// Everything Alpha-only goes through this file, so removing it is mostly done here. Every use elsewhere is a one-line call to
// a function below and carries the tag `TODO(post-Alpha SHER-10999)`: search for it to find them all.
//
// TODO(post-Alpha SHER-10999): once the real question banks are ingested and Evolve returns their ISBNs:
//  1. Pass-through helpers (nothing to change at their callers): make `withMedEdQuestionBankIsbns` and
//     `withMedEdPrimaryTaxonomies` return what they are given, and `getMedEdQuestionBankAppLinkBody` return `{}` (the token
//     will carry the real ISBNs); then drop `questionBankIsbns` from `AppLinkOutPostBody` and the three payload spreads.
//  2. Callers that need the real Step ISBNs themselves (they call `getMedEdQuestionBankIsbns`,
//     `getMedEdQuestionBankPrimaryTaxonomies` or the `MED_ED_STEP_*` constants): `StudentStudy.page.tsx` (taxonomies and My
//     Progress), `student-study.constants.ts` (the Steps of the dropdown) and `ResourceDrawer.component.tsx`. Read the ISBNs from
//     the course section's entitlements (`GET /courseSections/{id}`, the entries whose `parentIsbn` is the parent ISBN). A MedEd
//     course has more than two of them (the two Step banks and the mock exams, all of type `sherpath_component_nss`), so the
//     Steps cannot be told apart by product type: agree with Evolve how to identify Step 1 and Step 2 (today only their titles do).
//     Then delete the mock ISBNs and the two `get...` functions. This also removes `isMedEdQuestionBankAssignment`, the only
//     way an MCQ assignment is recognized today (its ISBN is a question-bank ISBN and an assignment has no product type):
//     agree how a real MCQ assignment is identified and update `isMedEdMcqAssignment` (`syllabus.utilities.tsx`).
//  3. Alpha-only behavior: `getCatalogProductFilters` (hides the product filter), the parent-ISBN branch of `getProductName`.
//  4. Detection: once the Evolve product type of a MedEd course is its own value again (`EvolveProductTypeKey.MED_ED`, or a
//     MedEd-specific one), make `isMedEdProduct` check only that product type, and delete
//     `MED_ED_PARENT_ISBN`, `hasIsbnIn` (if nothing else uses it), `addMedEdParentIsbn` and the parent-ISBN check in
//     `getProductName`.
//  5. Delete the tests that assert the mock values (this file's spec, `courseware.actions.fetchCatalog.spec.ts` and the
//     payload assertions in the Study Home specs).
export const MED_ED_STEP_1_QUESTION_BANK_ISBN = '1111444444441';
export const MED_ED_STEP_2_QUESTION_BANK_ISBN = '1111444444442';

/**
 * The MedEd parent ISBN, the one a MedEd course is created with. During Alpha the course's Evolve product type is the EAB
 * one, not `EvolveProductTypeKey.MED_ED`, so this ISBN is what tells a MedEd course apart.
 * Its child ISBNs (the real Step 1 and Step 2 ones) have no content yet and are deliberately not listed.
 */
export const MED_ED_PARENT_ISBN = '9780443271731';

/** The mock question-bank ISBNs of the MedEd course, Step 1 first. */
export const getMedEdQuestionBankIsbns = (): string[] => [
  MED_ED_STEP_1_QUESTION_BANK_ISBN,
  MED_ED_STEP_2_QUESTION_BANK_ISBN
];

/** The question-bank ISBNs as the primary taxonomies the Study Home, Resources and Course Plan pages load. */
export const getMedEdQuestionBankPrimaryTaxonomies = (): PrimaryTaxonomy[] => getMedEdQuestionBankIsbns().map(
  (isbn) => ({ isbn, isbnType: null, taxonomy: null })
);

export type MedEdProductLike = { isbn?: string | null; productTypeKey?: string | null; evolveProductTypeKey?: string | null };

const hasIsbnIn = (isbn: string | null | undefined, isbns: string[]): boolean => {
  return (isbn || '').split(',').some((value) => isbns.includes(value.trim()));
};

/**
 * Whether an entitlement or an Evolve product is a MedEd course product: its ISBN (a comma-joined list is allowed) is the parent
 * ISBN, or its product type is the MedEd one. During Alpha the product type of a MedEd course is the EAB one and cannot be told
 * apart from a real EAB course, so the parent ISBN is the check that matters; the type covers a course whose product type is
 * already `med_ed`. The question-bank ISBNs do not make a course MedEd: they only identify MedEd assignments
 * (`isMedEdQuestionBankAssignment`).
 * Entitlements and the Evolve products built from them (`getEvolveProducts`) carry the same ISBN and type, only the type key is
 * named differently (`evolveProductTypeKey` / `productTypeKey`), so this one check serves all of them.
 */
export const isMedEdProduct = (item: MedEdProductLike | null | undefined): boolean => {
  return item?.productTypeKey === EvolveProductTypeKey.MED_ED
    || item?.evolveProductTypeKey === EvolveProductTypeKey.MED_ED
    || hasIsbnIn(item?.isbn, [MED_ED_PARENT_ISBN]);
};

/** Whether an assignment reads its content from a MedEd question bank: its ISBN (a comma-joined list is allowed) is a mock bank one. */
export const isMedEdQuestionBankAssignment = (assignment: Pick<MedEdProductLike, 'isbn'> | null | undefined): boolean => {
  return hasIsbnIn(assignment?.isbn, getMedEdQuestionBankIsbns());
};

/** Whether a course is MedEd: any of its entitlements (`currentCourse.entitlements`) or Evolve products is the MedEd one. */
export const hasMedEdProduct = (items: (MedEdProductLike | null | undefined)[] | null | undefined): boolean => {
  return (items || []).some(isMedEdProduct);
};

/** The ISBNs to read content from: the mock question-bank ISBNs for a MedEd course, else the given (token) ISBNs. */
export const withMedEdQuestionBankIsbns = (isMedEd: boolean, isbns: string[] | null): string[] | null => {
  return isMedEd ? getMedEdQuestionBankIsbns() : isbns;
};

/** The primary taxonomies to load: the mock question-bank ones for a MedEd course, else the given ones. */
export const withMedEdPrimaryTaxonomies = (isMedEd: boolean, primaryTaxonomies: PrimaryTaxonomy[]): PrimaryTaxonomy[] => {
  return isMedEd ? getMedEdQuestionBankPrimaryTaxonomies() : primaryTaxonomies;
};

/**
 * The ISBNs of the user's token plus the MedEd parent ISBN when the entitlements have it, so that the products the MedEd
 * checks read never depend on the token.
 */
export const addMedEdParentIsbn = (tokenIsbns: string[], entitlementIsbns: (string | null | undefined)[]): string[] => {
  return entitlementIsbns.includes(MED_ED_PARENT_ISBN) && !tokenIsbns.includes(MED_ED_PARENT_ISBN)
    ? [...tokenIsbns, MED_ED_PARENT_ISBN]
    : tokenIsbns;
};

/** The part of a Q-Bank app-link body that carries the question-bank ISBNs (the CK app reads none from the token in Alpha). */
export const getMedEdQuestionBankAppLinkBody = (): { questionBankIsbns: string[] } => ({
  questionBankIsbns: getMedEdQuestionBankIsbns()
});
